import { Text } from '@avanta/shared/components/Text';
import { type User } from '@avanta/shared/types/users';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight, IdCard, Mail, Phone } from 'lucide-react';
import { PatientInfoRow } from './PatientInfoRow';

export type PatientCardPatient = Pick<
  User,
  'id' | 'firstName' | 'surname' | 'email' | 'contactPhone' | 'nif'
>;

interface PatientCardProps {
  patient: PatientCardPatient | undefined;
  onSelect?: (patient: PatientCardPatient) => void;
  isSelected?: boolean;
}

export const PatientCard = ({
  patient,
  onSelect,
  isSelected,
}: PatientCardProps): React.ReactElement | null => {
  if (!patient) return null;

  const fullName = [patient.firstName, patient.surname].filter(Boolean).join(' ') || patient?.email;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(patient)}
      aria-label={fullName}
      className={cn(
        'flex w-full shrink-0 items-center gap-4 overflow-hidden rounded-xl border border-teal-300 bg-white py-4 pr-4 pl-6 text-left transition-colors',
        !isSelected && 'hover:bg-plum-100 hover:shadow-box-sm hover:border-transparent'
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Text variant="text-md-medium" className="text-denim-800">
          {fullName}
        </Text>
        <div className="grid grid-cols-2 gap-4">
          {patient.email && <PatientInfoRow icon={Mail} value={patient.email} />}
          {patient.contactPhone && <PatientInfoRow icon={Phone} value={patient.contactPhone} />}
          {patient.nif && <PatientInfoRow icon={IdCard} value={patient.nif} />}
        </div>
      </div>
      {!isSelected && (
        <ChevronRight aria-hidden className="size-8 shrink-0 text-teal-600" strokeWidth={1.5} />
      )}
    </button>
  );
};
